/**
 * Renders the existing English interface in French without editing 2,300
 * source files.
 *
 * Every string in this application is a literal inside JSX. Retrofitting a
 * conventional i18n library means touching all of them, which is weeks of
 * mechanical edits and a broken build for most of it. Instead this wraps
 * `React.createElement` and maps text through a dictionary as elements are
 * created, so a screen nobody has translated by hand still comes out French.
 *
 * The honest limits, so the next person does not discover them the hard way:
 *
 *   - Matching is on whole strings. Text assembled at runtime
 *     (`` `${count} invoices` ``) will not match and stays English.
 *   - Prose the server sends — error messages, some field values — is not
 *     created here and is unaffected. That has to be fixed server side.
 *   - This is a presentation shim, not internationalisation. It carries no
 *     plurals, no gender agreement, no date or number formatting. Those come
 *     from antd's ConfigProvider and moment. The intended end state is
 *     react-i18next; this buys a French interface now without betting the
 *     delivery date on a full extraction.
 *
 * Requires the classic JSX transform (DISABLE_NEW_JSX_TRANSFORM=true). Under
 * the automatic runtime React.createElement is never called and this module
 * silently does nothing, so `assertClassicRuntime` below fails loudly instead.
 *
 * One trap worth knowing about: babel-loader's cache key does not include
 * that flag, so a tree compiled before it was set is reused after, and the
 * application comes up half French. `npm run build` clears the cache for
 * this reason; when switching the flag by hand, delete
 * node_modules/.cache/babel-loader or the result will not mean anything.
 */
import React from 'react';
import { isFrench } from '.';
import dictionary from './fr';

/**
 * Props whose value is read by a person rather than by the machine.
 *
 * Deliberately excludes `value`: on an input that is the user's own data, and
 * translating what someone typed would be a bug rather than a feature.
 */
const TRANSLATABLE_PROPS = [
    'placeholder',
    'title',
    'label',
    'okText',
    'cancelText',
    'emptyText',
    'tooltip',
    'alt',
    'description',
];

/** Elements whose text is content or code, where substitution would be wrong. */
const OPAQUE_ELEMENTS = ['code', 'pre', 'script', 'style', 'textarea'];

/**
 * Strings that reached the layer with no French for them.
 *
 * Exposed on `window.__missingTranslations` outside production so the
 * dictionary can be grown from what the application actually renders rather
 * than from what a regex over the source guessed it might. Walk the screens,
 * then read the set.
 */
const missing = new Set<string>();

const translateText = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return value;
    // A sentence wrapped across two source lines reaches us with the newline
    // and its indentation still in it. The browser collapses that whitespace
    // before anyone reads it, so the dictionary is keyed on the collapsed
    // form and long prose — which is exactly the text worth translating —
    // matches instead of silently staying English.
    const collapsed = trimmed.replace(/\s+/g, ' ');
    const hit = dictionary[trimmed] || dictionary[collapsed];
    if (!hit) {
        if (process.env.NODE_ENV !== 'production' && /[A-Za-z]{2}/.test(collapsed)) missing.add(collapsed);
        return value;
    }
    // Replacing the trimmed span keeps the surrounding whitespace, which JSX
    // relies on to space adjacent inline elements.
    return value.replace(trimmed, hit);
};

const translateChild = (child: unknown): unknown => (typeof child === 'string' ? translateText(child) : child);

let intercepted = false;

export const installTranslation = (): void => {
    if (!isFrench()) return;

    if (process.env.NODE_ENV !== 'production') {
        (window as any).__missingTranslations = () => [...missing].sort();
    }

    const createElement = React.createElement;

    (React as any).createElement = function (type: any, props: any, ...children: any[]) {
        intercepted = true;
        if (typeof type === 'string' && OPAQUE_ELEMENTS.indexOf(type) > -1) {
            return (createElement as any)(type, props, ...children);
        }

        let nextProps = props;
        if (props) {
            for (const prop of TRANSLATABLE_PROPS) {
                if (typeof props[prop] === 'string') {
                    const translated = translateText(props[prop]);
                    if (translated !== props[prop]) {
                        // Copy on first change only: most elements have nothing
                        // to translate and should not pay for a new object.
                        if (nextProps === props) nextProps = { ...props };
                        nextProps[prop] = translated;
                    }
                }
            }
            // Children handed through props rather than as arguments.
            if (!children.length && typeof props.children === 'string') {
                const translated = translateText(props.children);
                if (translated !== props.children) {
                    if (nextProps === props) nextProps = { ...props };
                    nextProps.children = translated;
                }
            }
        }

        return (createElement as any)(type, nextProps, ...children.map(translateChild));
    };
};

/**
 * Fails the build's smoke test rather than the demo.
 *
 * If CRA switches back to the automatic JSX runtime, translation stops
 * happening and every screen quietly reverts to English — the kind of
 * regression nobody notices until it is on a projector.
 */
export const assertClassicRuntime = (): void => {
    if (!isFrench() || intercepted) return;
    // eslint-disable-next-line no-console
    console.error(
        '[locale] French is selected but no element passed through the translation layer. ' +
            'JSX is compiling to the automatic runtime — set DISABLE_NEW_JSX_TRANSFORM=true ' +
            'and rebuild, or the entire interface will render in English.',
    );
};
